import { useMemo } from 'react';
import { useApiClient, useFetch, useFetchInfiniteES } from '@marageti/z4-lib';
import {
  FilterConstraint,
  FilterOp,
  SearchRequest,
  SearchResult,
} from '@marageti/z4-sdk/lib/entity';
import { Ticket } from '@marageti/z4-sdk/lib/tickets';

export type StatusFilterValue = 'pending' | 'dueToday' | 'overdue' | 'open' | 'completed' | 'canceled' | 'openAndCompleted' | '';

interface FilterOptions {
  statusFilter: StatusFilterValue;
  assignedTo?: string;
  tripRequestId?: string;
  travelerId?: string;
}

// Helper to build filter constraints for each filter type
const buildFilterConstraints = ({
  statusFilter,
  assignedTo,
  tripRequestId,
  travelerId,
}: FilterOptions): FilterConstraint[] => {
  const filters: FilterConstraint[] = [];

  filters.push({
    field: 'ticketType',
    op: FilterOp.FilterOpTerm,
    value: ['REMINDER'],
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endOfToday = new Date(today);
  endOfToday.setHours(23, 59, 59, 999);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(23, 59, 59, 999);

  // Status-based filters
  switch (statusFilter) {
    case '':
      // all items: no status filter
      break;
    case 'pending':
      // Pending: PENDING status + Due Today or Past
      filters.push({
        field: 'status',
        op: FilterOp.FilterOpTerm,
        value: ['PENDING'],
      });
      filters.push({
        field: 'dueAt',
        op: FilterOp.FilterOpLTE,
        value: [endOfToday.toISOString()],
      });
      break;
    case 'dueToday':
      // Due Today: PENDING status + Due Today
      filters.push({
        field: 'status',
        op: FilterOp.FilterOpTerm,
        value: ['PENDING'],
      });
      filters.push({
        field: 'dueAt',
        op: FilterOp.FilterOpGTE,
        value: [today.toISOString()],
      });
      filters.push({
        field: 'dueAt',
        op: FilterOp.FilterOpLTE,
        value: [endOfToday.toISOString()],
      });
      break;
    case 'overdue':
      // Overdue: PENDING status + Due Yesterday or Past
      filters.push({
        field: 'status',
        op: FilterOp.FilterOpTerm,
        value: ['PENDING'],
      });
      filters.push({
        field: 'dueAt',
        op: FilterOp.FilterOpLTE,
        value: [yesterday.toISOString()],
      });
      break;
    case 'open':
      // Open: All PENDING items
      filters.push({
        field: 'status',
        op: FilterOp.FilterOpTerm,
        value: ['PENDING'],
      });
      break;
    case 'completed':
      // Completed: All COMPLETED items
      filters.push({
        field: 'status',
        op: FilterOp.FilterOpTerm,
        value: ['COMPLETED'],
      });
      break;
    case 'canceled':
      // Canceled: All CANCELED items
      filters.push({
        field: 'status',
        op: FilterOp.FilterOpTerm,
        value: ['CANCELED'],
      });
      break;
    case 'openAndCompleted':
      // Open and Completed: PENDING + COMPLETED (for summary card)
      filters.push({
        field: 'status',
        op: FilterOp.FilterOpTerm,
        value: ['PENDING', 'COMPLETED'],
      });
      break;
    default:
      // Default to pending
      filters.push({
        field: 'status',
        op: FilterOp.FilterOpTerm,
        value: ['PENDING'],
      });
      filters.push({
        field: 'dueAt',
        op: FilterOp.FilterOpLTE,
        value: [endOfToday.toISOString()],
      });
  }

  // Add assignee filter if provided (assignees list model; mirrors issue ticket search)
  if (assignedTo) {
    filters.push({
      field: 'assignees.id',
      op: FilterOp.FilterOpTerm,
      value: [assignedTo],
    });
  }

  // Add tripRequestId filter if provided (for summary card)
  if (tripRequestId) {
    filters.push({
      field: 'tripRequestId',
      op: FilterOp.FilterOpTerm,
      value: [tripRequestId],
    });
  }

  // Add travelerId filter if provided (for summary card)
  if (travelerId) {
    filters.push({
      field: 'travelerId',
      op: FilterOp.FilterOpTerm,
      value: [travelerId],
    });
  }

  return filters;
};

interface UseRemindersSearchProps {
  assignedTo?: string;
  statusFilter?: StatusFilterValue;
  limit?: number;
  tripRequestId?: string;
  travelerId?: string;
}

const useRemindersSearch = ({
  assignedTo = '',
  statusFilter = 'pending',
  limit = 20,
  tripRequestId,
  travelerId,
}: UseRemindersSearchProps = {}) => {
  const apiClient = useApiClient();

  const getSearchRequest = (): SearchRequest => {
    const filterBy = buildFilterConstraints({
      statusFilter,
      assignedTo: assignedTo || undefined,
      tripRequestId,
      travelerId,
    });

    return {
      filterBy,
      pagination: { limit, page: 1 },
      sortBy: [{ field: 'dueAt', order: 'asc' }],
    };
  };

  const searchRequest = getSearchRequest();

  // Only fetch if tripRequestId/travelerId filters are not used,
  // or if at least one of them is provided
  const shouldFetch = !tripRequestId && !travelerId
    ? true
    : !!(tripRequestId || travelerId);

  const {
    data,
    error,
    hasMore,
    isLoading,
    loadMore,
    mutate,
  } = useFetchInfiniteES<Ticket>(
    shouldFetch
      ? apiClient.ticketsClient.searchTickets
      : () => Promise.resolve({ hits: [], totalHits: 0 } as SearchResult<Ticket>),
    searchRequest,
    { revalidateOnFocus: true, revalidateFirstPage: true },
  );

  const reminders = useMemo(() => (
    shouldFetch
      ? data?.flatMap((searchResult: SearchResult<Ticket>) => searchResult.hits) || []
      : []
  ), [data, shouldFetch]);

  const refetch = () => {
    if (shouldFetch) {
      return mutate();
    }
    return Promise.resolve();
  };

  return {
    reminders,
    totalHits: data?.[0]?.totalHits || 0,
    isLoading: shouldFetch ? isLoading : false,
    error: shouldFetch ? error : null,
    hasMore,
    loadMore,
    refetch,
  };
};

/**
 * Lightweight hook to get just the count of reminders matching the filters.
 * Uses limit: 0 to avoid fetching actual items.
 */
interface UseRemindersCountProps {
  assignedTo?: string;
  statusFilter?: StatusFilterValue;
  tripRequestId?: string;
  travelerId?: string;
}

export const useRemindersCount = ({
  assignedTo = '',
  statusFilter = 'pending',
  tripRequestId,
  travelerId,
}: UseRemindersCountProps = {}) => {
  const apiClient = useApiClient();

  const fetcher = async (): Promise<number> => {
    const filterBy = buildFilterConstraints({
      statusFilter,
      assignedTo: assignedTo || undefined,
      tripRequestId,
      travelerId,
    });

    const searchRequest: SearchRequest = {
      filterBy,
      pagination: { limit: 0, page: 1 },
    };

    const result: SearchResult<Ticket> = await apiClient.ticketsClient.searchTickets(searchRequest);
    return result.totalHits;
  };

  const shouldFetch = !!assignedTo;

  return useFetch<number>(
    fetcher,
    [assignedTo, statusFilter, tripRequestId, travelerId],
    shouldFetch,
    { revalidateOnFocus: true, refreshInterval: 1000 * 60 * 3 },
  );
};

export default useRemindersSearch;
